import { useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  BedDouble, Plus, Download, Search, Building2, Pencil, FileText, Receipt,
  Banknote, TrendingUp, Percent, DoorOpen, LogOut, Ban, CalendarCheck, CalendarPlus, CalendarRange, Loader2, Trash2, Copy, Zap, Undo2,
} from "lucide-react";
import api, { errMsg, downloadBlob } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProjects } from "@/context/ProjectContext";
import { PageHeader, SectionCard, StatCard, Money, Loading, EmptyState, Pagination } from "@/components/Common";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { formatRupiah, formatTanggal, formatTanggalWaktu, nowLocalInput } from "@/lib/format";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";

const BULAN = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const monthLabel = (m) => { const [y, mo] = String(m).split("-"); return `${BULAN[Number(mo) - 1] || mo} ${String(y).slice(2)}`; };
const compactRp = (v) => (v >= 1e6 ? `${(v / 1e6).toFixed(1)}jt` : v >= 1e3 ? `${Math.round(v / 1e3)}rb` : `${v}`);
const SERIES = { daily: "Sewa Harian", weekly: "Sewa Mingguan", monthly: "Paket Bulanan", ipl: "IPL", extra: "Biaya Tambahan" };
const ROOM_BAR = { terisi: "#334155", dipesan: "#c2410c", menunggu_masuk: "#0369a1" };
const ROOM_BUSY = { dipesan: "Dipesan (DP bulanan)", menunggu_masuk: "Lunas — menunggu masuk" };

const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const today = () => new Date().toISOString().slice(0, 10);
const PRESETS = [{ id: "month", label: "Bulan Ini" }, { id: "q", label: "3 Bulan" }, { id: "year", label: "Tahun Ini" }, { id: "custom", label: "Kustom" }];
const uuid = () => (crypto?.randomUUID ? crypto.randomUUID() : `gh-${Date.now()}-${Math.random()}`);
const toIsoWib = (v) => (v ? `${v}:00+07:00` : v);

const STAY_STATUS = {
  active: { label: "Menginap", bg: "#ecfdf5", text: "#047857", border: "#a7f3d0" },
  checked_out: { label: "Check-out", bg: "#f1f5f9", text: "#475569", border: "#cbd5e1" },
  extended: { label: "Diperpanjang", bg: "#fffbeb", text: "#b45309", border: "#fde68a" },
  cancelled: { label: "Dibatalkan", bg: "#fef2f2", text: "#b91c1c", border: "#fecaca" },
};
const BK_STATUS = {
  dp_held: { label: "DP Diterima (Aktif)", bg: "#fff7ed", text: "#c2410c" },
  converted: { label: "Lunas → Jadi Sewa", bg: "#ecfdf5", text: "#047857" },
  expired: { label: "Kedaluwarsa (DP Hangus)", bg: "#fef2f2", text: "#b91c1c" },
  cancelled: { label: "Dibatalkan", bg: "#f1f5f9", text: "#475569" },
};
function Pill({ map, value }) {
  const s = map[value] || { label: value, bg: "#f1f5f9", text: "#475569", border: "#cbd5e1" };
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap"
      style={{ backgroundColor: s.bg, color: s.text, borderColor: s.border || s.bg }} data-testid={`gh-pill-${value}`}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: s.text }} />{s.label}
    </span>
  );
}

async function openReceiptById(payment_id) {
  try {
    const res = await api.get(`/guesthouse/payments/${payment_id}/receipt`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  } catch (e) { toast.error(errMsg(e)); }
}
async function openLatestReceipt(params) {
  try {
    const { data } = await api.get("/guesthouse/payments", { params });
    if (!data.length) { toast.info("Belum ada struk."); return; }
    await openReceiptById(data[0].id);
  } catch (e) { toast.error(errMsg(e)); }
}

export default function GuestHouse() {
  const { isSuperadmin, canManage } = useAuth();
  const { projects, projectId, projectParam } = useProjects();
  const qc = useQueryClient();

  const [activeBuilding, setActiveBuilding] = useState(null);
  const [preset, setPreset] = useState("month");
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [recordRoom, setRecordRoom] = useState(null);
  const [weeklyRoom, setWeeklyRoom] = useState(null);
  const [extendStay, setExtendStay] = useState(null);
  const [monthlyRoom, setMonthlyRoom] = useState(null);
  const [editRoom, setEditRoom] = useState(null);
  const [dupRoom, setDupRoom] = useState(null);
  const [filters, setFilters] = useState({ q: "", status: "all", method: "all", kind: "all" });
  const [page, setPage] = useState(1);
  const [settleBooking, setSettleBooking] = useState(null);
  const [moveinLease, setMoveinLease] = useState(null);
  const [extLease, setExtLease] = useState(null);
  const [exitLease, setExitLease] = useState(null);
  const [editBuilding, setEditBuilding] = useState(null);
  const [directRoom, setDirectRoom] = useState(null);
  const [depositLease, setDepositLease] = useState(null);

  const applyPreset = (id) => {
    setPreset(id);
    const now = new Date();
    if (id === "month") { setFrom(monthStart()); setTo(today()); }
    else if (id === "q") { setFrom(new Date(now.getFullYear(), now.getMonth() - 2, 1).toISOString().slice(0, 10)); setTo(today()); }
    else if (id === "year") { setFrom(new Date(now.getFullYear(), 0, 1).toISOString().slice(0, 10)); setTo(today()); }
  };

  const { data: buildings = [] } = useQuery({
    queryKey: ["gh-buildings", projectId],
    queryFn: async () => (await api.get("/guesthouse/buildings", { params: projectParam })).data,
  });
  const currentBuilding = buildings.find((b) => b.id === activeBuilding) || buildings[0] || null;

  const { data: rooms = [], isLoading: roomsLoading } = useQuery({
    queryKey: ["gh-rooms", currentBuilding?.id],
    queryFn: async () => (await api.get("/guesthouse/rooms", { params: { building_id: currentBuilding.id } })).data,
    enabled: !!currentBuilding,
  });

  const { data: recap } = useQuery({
    queryKey: ["gh-recap", projectId, from, to],
    queryFn: async () => (await api.get("/guesthouse/recap", { params: { ...projectParam, date_from: from, date_to: to } })).data,
  });

  const { data: trend = [] } = useQuery({
    queryKey: ["gh-trend", projectId],
    queryFn: async () => (await api.get("/guesthouse/trend", { params: { ...projectParam, months: 6 } })).data,
  });

  const stayParams = useMemo(() => ({
    ...projectParam, q: filters.q || undefined,
    status: filters.status === "all" ? undefined : filters.status,
    method: filters.method === "all" ? undefined : filters.method,
    kind: filters.kind === "all" ? undefined : filters.kind,
    page, page_size: 15,
  }), [projectParam, filters, page]);

  const { data: staysData, isLoading: staysLoading } = useQuery({
    queryKey: ["gh-stays", stayParams],
    queryFn: async () => (await api.get("/guesthouse/stays", { params: stayParams })).data,
  });

  const { data: bookings = [], isLoading: bookingsLoading } = useQuery({
    queryKey: ["gh-bookings", projectId],
    queryFn: async () => (await api.get("/guesthouse/bookings", { params: projectParam })).data,
  });
  const { data: leases = [], isLoading: leasesLoading } = useQuery({
    queryKey: ["gh-leases", projectId],
    queryFn: async () => (await api.get("/guesthouse/leases", { params: projectParam })).data,
  });

  const occupancy = recap && recap.rooms_total ? Math.round((recap.rooms_occupied / recap.rooms_total) * 100) : 0;

  const deleteGhRoom = async (r) => {
    if (!window.confirm(`Hapus Kamar ${r.number}? Hanya dapat dihapus jika tersedia & tanpa riwayat transaksi.`)) return;
    try {
      await api.delete(`/guesthouse/rooms/${r.id}`);
      toast.success("Kamar dihapus.");
      qc.invalidateQueries({ queryKey: ["gh-rooms", currentBuilding?.id] });
      qc.invalidateQueries({ queryKey: ["gh-buildings"] });
      qc.invalidateQueries({ queryKey: ["gh-recap"] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const deleteGhBuilding = async (b) => {
    if (!window.confirm(`Hapus gedung "${b.name}"? Hanya dapat dihapus jika tidak ada kamar di dalamnya.`)) return;
    try {
      await api.delete(`/guesthouse/buildings/${b.id}`);
      toast.success("Gedung dihapus.");
      setActiveBuilding(null);
      qc.invalidateQueries({ queryKey: ["gh-buildings"] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/guesthouse/stays/export.csv", {
        params: { ...projectParam, q: filters.q || undefined, status: filters.status === "all" ? undefined : filters.status, method: filters.method === "all" ? undefined : filters.method, kind: filters.kind === "all" ? undefined : filters.kind },
        responseType: "blob",
      });
      downloadBlob(res.data, "guest-house.csv");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const checkout = useMutation({
    mutationFn: async (id) => (await api.post(`/guesthouse/stays/${id}/checkout`)).data,
    onSuccess: () => { toast.success("Tamu berhasil check-out."); qc.invalidateQueries(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const cancelStay = useMutation({
    mutationFn: async (id) => (await api.post(`/guesthouse/stays/${id}/cancel`)).data,
    onSuccess: () => { toast.success("Transaksi dibatalkan."); qc.invalidateQueries(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const cancelBooking = useMutation({
    mutationFn: async (id) => (await api.post(`/guesthouse/bookings/${id}/cancel`)).data,
    onSuccess: () => { toast.success("Booking dibatalkan — DP hangus menjadi pendapatan pengelola."); qc.invalidateQueries(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const activeBookings = bookings.filter((b) => b.status === "dp_held");

  return (
    <div className="space-y-6">
      <PageHeader
        title="Guest House"
        description="Gedung milik pengelola — pendapatan 100% pengelola, terpisah dari laporan investor."
        actions={
          <>
            <Button variant="outline" onClick={exportCsv} data-testid="gh-export-csv"><Download className="mr-2 h-4 w-4" /> Ekspor CSV</Button>
            {canManage && <CreateBuilding projects={projects} onDone={() => qc.invalidateQueries({ queryKey: ["gh-buildings"] })} />}
          </>
        }
      />

      {/* Recap filter + cards */}
      <SectionCard testid="gh-recap-filter" className="!p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <Button key={p.id} variant={preset === p.id ? "default" : "outline"} size="sm" onClick={() => applyPreset(p.id)} data-testid={`gh-preset-${p.id}`}>{p.label}</Button>
            ))}
          </div>
          {preset === "custom" && (
            <div className="flex items-end gap-2">
              <div><Label className="text-xs">Dari</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9" data-testid="gh-date-from" /></div>
              <div><Label className="text-xs">Sampai</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9" data-testid="gh-date-to" /></div>
            </div>
          )}
        </div>
      </SectionCard>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
        <StatCard testid="gh-total-income" label="Total Pendapatan" value={<Money value={recap?.total_income} />} icon={Banknote} accent="#047857" hint="Pendapatan pengelola pada periode" />
        <StatCard testid="gh-daily-income" label="Sewa Harian" value={<Money value={recap?.daily_income} />} icon={TrendingUp} accent="#dd6030" hint={`${recap?.nights_sold || 0} malam · ${recap?.stays_count || 0} transaksi`} />
        <StatCard testid="gh-weekly-income" label="Sewa Mingguan" value={<Money value={recap?.weekly_income} />} icon={CalendarRange} accent="#ca8a04" hint={`${recap?.weeks_sold || 0} minggu · ${recap?.weekly_count || 0} transaksi`} />
        <StatCard testid="gh-monthly-income" label="Bulanan + IPL" value={<Money value={(recap?.monthly_income || 0) + (recap?.ipl_income || 0) + (recap?.extra_income || 0)} />} icon={CalendarCheck} accent="#0369a1" hint="Paket 1/3/6/12 bln (termasuk IPL & biaya tambahan)" />
        <StatCard testid="gh-occupancy" label="Okupansi Kamar" value={`${occupancy}%`} icon={Percent} accent="#7e22ce" hint={`${recap?.rooms_occupied || 0} dari ${recap?.rooms_total || 0} kamar terisi`} />
      </div>

      {/* Trend chart */}
      <SectionCard title="Tren Pendapatan Guest House" description="Pendapatan pengelola per bulan (6 bulan terakhir)." testid="gh-trend-card">
        {trend.filter((t) => t.daily || t.weekly || t.monthly || t.ipl || t.extra).length === 0 ? (
          <EmptyState title="Belum ada data tren" description="Data muncul setelah ada transaksi sewa guest house." icon={TrendingUp} />
        ) : (
          <div className="h-72 w-full" data-testid="gh-trend-chart">
            <ResponsiveContainer width="100%" height="100%" minHeight={288}>
              <BarChart data={trend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f5" vertical={false} />
                <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={compactRp} tick={{ fontSize: 12 }} width={48} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v, n) => [formatRupiah(v), SERIES[n] || n]} labelFormatter={monthLabel} cursor={{ fill: "#f8fafc" }} />
                <Legend formatter={(v) => SERIES[v] || v} />
                <Bar dataKey="daily" stackId="a" fill="#dd6030" />
                <Bar dataKey="weekly" stackId="a" fill="#eab308" />
                <Bar dataKey="monthly" stackId="a" fill="#0369a1" />
                <Bar dataKey="ipl" stackId="a" fill="#7e22ce" />
                <Bar dataKey="extra" stackId="a" fill="#0f766e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </SectionCard>

      {/* Buildings & rooms */}
      {buildings.length === 0 ? (
        <EmptyState title="Belum ada gedung guest house" description={canManage ? "Tambah gedung terlebih dahulu." : "Hubungi superadmin untuk menambah gedung."} icon={Building2} />
      ) : (
        <SectionCard title="Gedung & Kamar" testid="gh-buildings-section">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            {buildings.map((b) => (
              <button key={b.id} onClick={() => setActiveBuilding(b.id)} data-testid={`gh-building-tab-${b.code}`}
                className={`rounded-lg border px-3.5 py-2 text-sm font-medium transition-colors ${currentBuilding?.id === b.id ? "border-primary bg-primary text-white" : "border-border bg-card hover:bg-muted"}`}>
                {b.name} <span className={currentBuilding?.id === b.id ? "text-white/70" : "text-muted-foreground"}>· {b.room_count} kamar</span>
              </button>
            ))}
          </div>
          {currentBuilding && (
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div className="text-xs text-muted-foreground">Proyek: <span className="font-medium text-foreground">{currentBuilding.project_name}</span> · {currentBuilding.address || "-"}</div>
              {canManage && (
                <div className="flex flex-wrap items-center gap-2">
                  <CreateRoom buildingId={currentBuilding.id} onDone={() => qc.invalidateQueries({ queryKey: ["gh-rooms", currentBuilding.id] })} />
                  <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => setEditBuilding(currentBuilding)} data-testid={`gh-building-edit-${currentBuilding.code}`}>
                    <Pencil className="mr-1.5 h-4 w-4" /> Ubah Gedung
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8 px-2 text-destructive hover:text-destructive" onClick={() => deleteGhBuilding(currentBuilding)} data-testid={`gh-building-delete-${currentBuilding.code}`}>
                    <Trash2 className="mr-1.5 h-4 w-4" /> Hapus Gedung
                  </Button>
                </div>
              )}
            </div>
          )}
          {roomsLoading ? <Loading /> : rooms.length === 0 ? (
            <EmptyState title="Belum ada kamar" description={canManage ? "Tambah kamar guest house." : "Belum ada kamar."} icon={DoorOpen} />
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {rooms.map((r) => (
                <div key={r.id} className="overflow-hidden rounded-xl border border-border bg-card" data-testid={`gh-room-card-${r.number}`}>
                  <div className="h-1.5" style={{ backgroundColor: ROOM_BAR[r.rental_status] || "#047857" }} />
                  <div className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="text-lg font-bold">Kamar {r.number}</div>
                      {canManage && (
                        <div className="flex items-center gap-2">
                          <button onClick={() => setEditRoom(r)} className="text-muted-foreground hover:text-foreground" data-testid={`gh-room-edit-${r.number}`}><Pencil className="h-4 w-4" /></button>
                          <button onClick={() => setDupRoom(r)} className="text-muted-foreground hover:text-foreground" title="Duplikat kamar" data-testid={`gh-room-duplicate-${r.number}`}><Copy className="h-4 w-4" /></button>
                          <button onClick={() => deleteGhRoom(r)} className="text-muted-foreground hover:text-destructive" data-testid={`gh-room-delete-${r.number}`}><Trash2 className="h-4 w-4" /></button>
                        </div>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <StatusBadge kind="rental" value={r.rental_status} />
                      <StatusBadge kind="condition" value={r.physical_condition} />
                    </div>
                    <div className="mt-3 text-sm">
                      <span className="font-semibold"><Money value={r.daily_price} /></span><span className="text-muted-foreground"> / malam</span>
                      {r.pricing && <div className="text-xs text-muted-foreground">Bulanan 3 bln: <Money value={r.pricing["3"]} /></div>}
                    </div>
                    {r.current_guest_name && !r.current_stay && (
                      <div className="mt-2 text-xs text-muted-foreground"><span className="font-medium text-foreground">{r.current_guest_name}</span></div>
                    )}
                    {r.current_stay && (
                      <div className="mt-2 text-xs text-muted-foreground"><span className="font-medium text-foreground">{r.current_stay.guest_name}</span> · s/d {formatTanggal(r.current_stay.check_out)}</div>
                    )}
                    <div className="mt-3 space-y-2">
                      {r.rental_status === "tersedia" ? (
                        <>
                          <div className={`grid gap-2 ${r.pricing ? "grid-cols-3" : "grid-cols-2"}`}>
                            <Button size="sm" variant="outline" className="px-2" onClick={() => setRecordRoom({ ...r, building_name: currentBuilding?.name })} data-testid={`gh-record-stay-${r.number}`}>
                              <BedDouble className="mr-1 h-4 w-4" /> Harian
                            </Button>
                            <Button size="sm" variant="outline" className="px-2" onClick={() => setWeeklyRoom({ ...r, building_name: currentBuilding?.name })} data-testid={`gh-record-weekly-${r.number}`}>
                              <CalendarRange className="mr-1 h-4 w-4" /> Mingguan
                            </Button>
                            {r.pricing && (
                              <Button size="sm" className="px-2" onClick={() => setMonthlyRoom({ ...r, building_name: currentBuilding?.name })} data-testid={`gh-book-monthly-${r.number}`}>
                                <CalendarCheck className="mr-1 h-4 w-4" /> Bulanan
                              </Button>
                            )}
                          </div>
                          {r.pricing && (
                            <Button size="sm" variant="secondary" className="w-full" onClick={() => setDirectRoom({ ...r, building_name: currentBuilding?.name })} data-testid={`gh-direct-rent-${r.number}`}>
                              <Zap className="mr-1.5 h-4 w-4" /> Langsung Sewa — Tanpa DP
                            </Button>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="w-full rounded-lg bg-muted/50 px-3 py-2 text-center text-xs text-muted-foreground" data-testid={`gh-room-busy-${r.number}`}>{ROOM_BUSY[r.rental_status] || "Sedang terisi"}</div>
                          {r.current_stay?.kind === "weekly" && (
                            <Button size="sm" variant="outline" className="w-full" onClick={() => setExtendStay({ ...r.current_stay, room_number: r.number, building_name: currentBuilding?.name })} data-testid={`gh-room-extend-weekly-${r.number}`}>
                              <CalendarPlus className="mr-1.5 h-4 w-4" /> Perpanjang Mingguan
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      )}

      {/* Transactions tabs */}
      <Tabs defaultValue="harian">
        <TabsList>
          <TabsTrigger value="harian" data-testid="gh-tab-harian">Sewa Harian & Mingguan</TabsTrigger>
          <TabsTrigger value="bulanan" data-testid="gh-tab-bulanan">Sewa Bulanan ({activeBookings.length + leases.filter((l) => l.status === "active" || l.status === "awaiting_movein").length})</TabsTrigger>
        </TabsList>

        {/* Harian */}
        <TabsContent value="harian" className="mt-4">
          <SectionCard title="Transaksi Sewa Harian & Mingguan" testid="gh-stays-section">
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[180px]">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input placeholder="Cari nama tamu..." className="h-9 pl-9" value={filters.q} onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, q: e.target.value })); }} data-testid="gh-stay-search" />
              </div>
              <Select value={filters.status} onValueChange={(v) => { setPage(1); setFilters((f) => ({ ...f, status: v })); }}>
                <SelectTrigger className="h-9 w-[160px]" data-testid="gh-stay-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Status</SelectItem>
                  <SelectItem value="active">Menginap</SelectItem>
                  <SelectItem value="checked_out">Check-out</SelectItem>
                  <SelectItem value="extended">Diperpanjang</SelectItem>
                  <SelectItem value="cancelled">Dibatalkan</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.kind} onValueChange={(v) => { setPage(1); setFilters((f) => ({ ...f, kind: v })); }}>
                <SelectTrigger className="h-9 w-[150px]" data-testid="gh-stay-filter-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Jenis</SelectItem>
                  <SelectItem value="daily">Harian</SelectItem>
                  <SelectItem value="weekly">Mingguan</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.method} onValueChange={(v) => { setPage(1); setFilters((f) => ({ ...f, method: v })); }}>
                <SelectTrigger className="h-9 w-[150px]" data-testid="gh-stay-filter-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Metode</SelectItem>
                  <SelectItem value="transfer">Transfer Bank</SelectItem>
                  <SelectItem value="cash">Tunai</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {staysLoading ? <Loading /> : !staysData?.items?.length ? (
              <EmptyState title="Belum ada transaksi" description="Catat tamu harian atau mingguan dari kartu kamar di atas." icon={Receipt} />
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="gh-stays-table">
                    <thead>
                      <tr className="border-b border-border text-left text-xs text-muted-foreground">
                        <th className="py-2 pr-3 font-medium">Tamu</th><th className="py-2 pr-3 font-medium">Kamar</th>
                        <th className="py-2 pr-3 font-medium">Periode</th><th className="py-2 pr-3 font-medium">Lama</th>
                        <th className="py-2 pr-3 font-medium">Total</th><th className="py-2 pr-3 font-medium">Metode</th>
                        <th className="py-2 pr-3 font-medium">Status</th><th className="py-2 pr-3 font-medium text-right">Aksi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {staysData.items.map((s) => (
                        <tr key={s.id} data-testid={`gh-stay-row-${s.id}`}>
                          <td className="py-2.5 pr-3"><div className="font-medium">{s.guest_name}</div><div className="text-xs text-muted-foreground">{s.building_name}</div></td>
                          <td className="py-2.5 pr-3">{s.room_number}</td>
                          <td className="py-2.5 pr-3 text-xs">{formatTanggal(s.check_in)} <span className="text-muted-foreground">s/d</span> {formatTanggal(s.check_out)}</td>
                          <td className="py-2.5 pr-3" data-testid={`gh-stay-duration-${s.id}`}>
                            {s.kind === "weekly" ? `${s.weeks} minggu` : `${s.nights} malam`}
                            <div className={`mt-0.5 inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${s.kind === "weekly" ? "bg-amber-50 text-amber-700" : "bg-orange-50 text-orange-700"}`}>{s.kind_label || "Harian"}</div>
                            {s.parent_stay_id && <div className="mt-0.5 ml-1 inline-block rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-700" data-testid={`gh-stay-extension-badge-${s.id}`}>Perpanjangan</div>}
                          </td>
                          <td className="py-2.5 pr-3 font-semibold tabular-nums"><Money value={s.total} /></td>
                          <td className="py-2.5 pr-3 text-xs">{s.method_label}</td>
                          <td className="py-2.5 pr-3"><Pill map={STAY_STATUS} value={s.status} /></td>
                          <td className="py-2.5 pr-3">
                            <div className="flex items-center justify-end gap-1">
                              <Button size="sm" variant="ghost" className="h-7 px-2" title="Struk" onClick={() => openReceiptStay(s.id)} data-testid={`gh-receipt-${s.id}`}><FileText className="h-4 w-4" /></Button>
                              {s.status === "active" && s.kind === "weekly" && <Button size="sm" variant="ghost" className="h-7 px-2 text-amber-700" title="Perpanjang Mingguan" onClick={() => setExtendStay(s)} data-testid={`gh-extend-stay-${s.id}`}><CalendarPlus className="h-4 w-4" /></Button>}
                              {s.status === "active" && <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-700" title="Check-out" onClick={() => checkout.mutate(s.id)} data-testid={`gh-checkout-${s.id}`}><LogOut className="h-4 w-4" /></Button>}
                              {isSuperadmin && s.status !== "cancelled" && <Button size="sm" variant="ghost" className="h-7 px-2 text-destructive" title="Batalkan" onClick={() => { if (window.confirm("Batalkan transaksi ini?")) cancelStay.mutate(s.id); }} data-testid={`gh-cancel-${s.id}`}><Ban className="h-4 w-4" /></Button>}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination page={staysData.page} total={staysData.total} pageSize={staysData.page_size} onPage={setPage} />
              </>
            )}
          </SectionCard>
        </TabsContent>

        {/* Bulanan */}
        <TabsContent value="bulanan" className="mt-4 space-y-4">
          <SectionCard title="Booking DP (Bulanan)" description="DP ditahan 96 jam. Lanjutkan ke pelunasan untuk menjadi sewa aktif." testid="gh-bookings-section">
            {bookingsLoading ? <Loading /> : activeBookings.length === 0 ? (
              <EmptyState title="Belum ada booking DP aktif" description="Mulai booking bulanan dari kartu kamar (tombol Bulanan)." icon={CalendarCheck} />
            ) : (
              <div className="space-y-3">
                {activeBookings.map((b) => (
                  <div key={b.id} className="rounded-xl border border-border p-4" data-testid={`gh-booking-row-${b.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2"><span className="font-semibold">{b.guest_name}</span><Pill map={BK_STATUS} value={b.status} /></div>
                        <div className="mt-0.5 text-xs text-muted-foreground">{b.building_name} · Kamar {b.room_number} · {b.package_months} bln · {b.whatsapp}</div>
                        <div className="mt-2 flex flex-wrap gap-4 text-sm">
                          <span>Total: <Money value={b.total_billed} /></span><span>DP: <Money value={b.dp_amount} /></span>
                          <span className="font-semibold">Sisa: <Money value={b.remaining} /></span>
                          <span className="text-muted-foreground">Batas: {formatTanggalWaktu(b.deadline_at)}</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" className="h-8 px-2" title="Struk DP" onClick={() => openLatestReceipt({ booking_id: b.id })} data-testid={`gh-booking-receipt-${b.id}`}><FileText className="h-4 w-4" /></Button>
                        {isSuperadmin && <Button size="sm" variant="ghost" className="h-8 px-2 text-destructive" title="Batalkan (DP hangus)" onClick={() => { if (window.confirm("Batalkan booking? DP Rp500.000 hangus menjadi pendapatan pengelola.")) cancelBooking.mutate(b.id); }} data-testid={`gh-booking-cancel-${b.id}`}><Ban className="h-4 w-4" /></Button>}
                        <Button size="sm" onClick={() => setSettleBooking(b)} data-testid={`gh-settle-${b.id}`}>Catat Pelunasan</Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard title="Sewa Bulanan Aktif & Riwayat" testid="gh-leases-section">
            {leasesLoading ? <Loading /> : leases.length === 0 ? (
              <EmptyState title="Belum ada sewa bulanan" icon={DoorOpen} />
            ) : (
              <div className="space-y-3">
                {leases.map((l) => (
                  <div key={l.id} className="rounded-xl border border-border p-4" data-testid={`gh-lease-row-${l.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold">{l.guest_name}</span><StatusBadge kind="lease" value={l.status} />
                          {l.direct_rent && <span className="rounded-full bg-orange-50 px-2 py-0.5 text-xs font-semibold text-orange-700" data-testid={`gh-lease-direct-badge-${l.id}`}>Langsung Sewa</span>}
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">{l.building_name} · Kamar {l.room_number} · {l.package_months} bln</div>
                        <div className="mt-2 text-sm">Periode: <span className="font-medium">{l.period_label}</span></div>
                        {l.status === "awaiting_movein" && l.plan_movein_date && <div className="text-xs text-muted-foreground">Rencana masuk: {formatTanggal(l.plan_movein_date)}</div>}
                        {l.settlement_date && <div className="text-xs text-muted-foreground">Pelunasan: {formatTanggal(l.settlement_date)} · Sewa <Money value={l.rent_net ?? l.rent_price} /> + IPL <Money value={l.ipl_total} /></div>}
                        {(l.discount > 0 || l.extra_fees > 0 || l.deposit > 0) && (
                          <div className="text-xs text-muted-foreground" data-testid={`gh-lease-extras-${l.id}`}>
                            {l.discount > 0 && <span>Diskon <Money value={l.discount} /> · </span>}
                            {l.extra_fees > 0 && <span>Biaya tambahan <Money value={l.extra_fees} /> · </span>}
                            {l.deposit > 0 && <span>Deposit <Money value={l.deposit} /></span>}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="ghost" className="h-8 px-2" title="Struk" onClick={() => openLatestReceipt({ lease_id: l.id })} data-testid={`gh-lease-receipt-${l.id}`}><FileText className="h-4 w-4" /></Button>
                        {l.status === "awaiting_movein" && <Button size="sm" onClick={() => setMoveinLease(l)} data-testid={`gh-movein-${l.id}`}><DoorOpen className="mr-1.5 h-4 w-4" /> Catat Masuk</Button>}
                        {l.status === "active" && <Button size="sm" variant="outline" onClick={() => setExtLease(l)} data-testid={`gh-extend-${l.id}`}><CalendarPlus className="mr-1.5 h-4 w-4" /> Perpanjang</Button>}
                        {l.status === "active" && <Button size="sm" variant="outline" className="text-destructive" onClick={() => setExitLease(l)} data-testid={`gh-exit-${l.id}`}><LogOut className="mr-1.5 h-4 w-4" /> Keluar Awal</Button>}
                        {["ended", "early_exit"].includes(l.status) && l.deposit > 0 && !l.deposit_refunded && <Button size="sm" variant="outline" onClick={() => setDepositLease(l)} data-testid={`gh-deposit-refund-${l.id}`}><Undo2 className="mr-1.5 h-4 w-4" /> Kembalikan Deposit</Button>}
                        {l.deposit_refunded && <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700" data-testid={`gh-deposit-refunded-${l.id}`}>Deposit dikembalikan</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </TabsContent>
      </Tabs>

      {recordRoom && <RecordStayDialog room={recordRoom} onClose={() => setRecordRoom(null)} onDone={() => { setRecordRoom(null); qc.invalidateQueries(); }} />}
      {weeklyRoom && <WeeklyStayDialog room={weeklyRoom} onClose={() => setWeeklyRoom(null)} onDone={() => { setWeeklyRoom(null); qc.invalidateQueries(); }} />}
      {extendStay && <ExtendWeeklyDialog stay={extendStay} onClose={() => setExtendStay(null)} onDone={() => { setExtendStay(null); qc.invalidateQueries(); }} />}
      {monthlyRoom && <MonthlyBookingDialog room={monthlyRoom} onClose={() => setMonthlyRoom(null)} onDone={() => { setMonthlyRoom(null); qc.invalidateQueries(); }} />}
      {editRoom && <EditRoomDialog room={editRoom} isSuperadmin={canManage} onClose={() => setEditRoom(null)} onDone={() => { setEditRoom(null); qc.invalidateQueries(); }} />}
      {dupRoom && <GhDuplicateRoomDialog room={dupRoom} onClose={() => setDupRoom(null)} onDone={() => { setDupRoom(null); qc.invalidateQueries(); }} />}
      {settleBooking && <SettlementDialog booking={settleBooking} onClose={() => setSettleBooking(null)} onDone={() => { setSettleBooking(null); qc.invalidateQueries(); }} />}
      {moveinLease && <MoveinDialog lease={moveinLease} onClose={() => setMoveinLease(null)} onDone={() => { setMoveinLease(null); qc.invalidateQueries(); }} />}
      {extLease && <ExtensionDialog lease={extLease} onClose={() => setExtLease(null)} onDone={() => { setExtLease(null); qc.invalidateQueries(); }} />}
      {exitLease && <ExitDialog lease={exitLease} onClose={() => setExitLease(null)} onDone={() => { setExitLease(null); qc.invalidateQueries(); }} />}
      {directRoom && <GhDirectRentDialog room={directRoom} onClose={() => setDirectRoom(null)} onDone={() => { setDirectRoom(null); qc.invalidateQueries(); }} />}
      {depositLease && <GhDepositRefundDialog lease={depositLease} onClose={() => setDepositLease(null)} onDone={() => { setDepositLease(null); qc.invalidateQueries(); }} />}
      {editBuilding && <EditBuilding building={editBuilding} onClose={() => setEditBuilding(null)} onDone={() => { setEditBuilding(null); qc.invalidateQueries({ queryKey: ["gh-buildings"] }); }} />}
    </div>
  );

  async function openReceiptStay(stayId) {
    try {
      const res = await api.get(`/guesthouse/stays/${stayId}/receipt`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { toast.error(errMsg(e)); }
  }
}

function SummaryRow({ label, value, bold }) {
  return <div className={`flex justify-between ${bold ? "font-semibold" : ""}`}><span className={bold ? "" : "text-muted-foreground"}>{label}</span><Money value={value} /></div>;
}

function EditBuilding({ building, onClose, onDone }) {
  const [f, setF] = useState({ name: building.name || "", address: building.address || "" });
  const [loading, setLoading] = useState(false);
  const save = async () => {
    if (!f.name.trim()) { toast.error("Nama gedung wajib diisi."); return; }
    setLoading(true);
    try { await api.patch(`/guesthouse/buildings/${building.id}`, { name: f.name.trim(), address: f.address.trim() }); toast.success("Gedung diperbarui."); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Ubah Gedung Guest House</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nama Gedung</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="gh-building-edit-name" /></div>
          <div><Label>Alamat</Label><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="gh-building-edit-address" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={loading} data-testid="gh-building-edit-save">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateBuilding({ projects, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ project_id: "", code: "", name: "", address: "" });
  const save = async () => {
    if (!f.project_id) { toast.error("Pilih proyek."); return; }
    try { await api.post("/guesthouse/buildings", f); toast.success("Gedung ditambahkan."); setOpen(false); setF({ project_id: "", code: "", name: "", address: "" }); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="gh-add-building-button"><Plus className="mr-2 h-4 w-4" /> Tambah Gedung</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Tambah Gedung Guest House</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Proyek</Label>
            <Select value={f.project_id} onValueChange={(v) => setF({ ...f, project_id: v })}>
              <SelectTrigger data-testid="gh-building-project"><SelectValue placeholder="Pilih proyek" /></SelectTrigger>
              <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Kode Gedung</Label><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="gh-building-code" /></div>
          <div><Label>Nama Gedung</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="gh-building-name" /></div>
          <div><Label>Alamat</Label><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="gh-building-address" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={save} data-testid="gh-building-save">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GhDuplicateRoomDialog({ room, onClose, onDone }) {
  const [numbersRaw, setNumbersRaw] = useState("");
  const [targetBuilding, setTargetBuilding] = useState(room.building_id);
  const [daily, setDaily] = useState(room.daily_price);
  const [p, setP] = useState({ 1: room.pricing?.["1"] || "", 3: room.pricing?.["3"] || "", 6: room.pricing?.["6"] || "", 12: room.pricing?.["12"] || "", ipl: room.ipl_monthly || "" });
  const [loading, setLoading] = useState(false);
  const { data: buildings = [] } = useQuery({ queryKey: ["gh-all-buildings"], queryFn: async () => (await api.get("/guesthouse/buildings")).data });
  const parsed = numbersRaw.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
  const save = async () => {
    if (parsed.length === 0) { toast.error("Masukkan minimal satu nomor kamar baru."); return; }
    if (!targetBuilding) { toast.error("Pilih gedung tujuan."); return; }
    setLoading(true);
    try {
      const pricing = (p[1] && p[3] && p[6] && p[12]) ? { 1: Number(p[1]), 3: Number(p[3]), 6: Number(p[6]), 12: Number(p[12]) } : null;
      const res = await api.post(`/guesthouse/rooms/${room.id}/duplicate`, {
        numbers: parsed, building_id: targetBuilding, daily_price: Number(daily), pricing, ipl_monthly: Number(p.ipl || 0),
      });
      const toB = buildings.find((b) => b.id === targetBuilding);
      toast.success(`${res.data.length} kamar dibuat${toB ? ` di ${toB.name}` : ""} dari tipe Kamar ${room.number}.`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="gh-duplicate-dialog">
        <DialogHeader><DialogTitle>Duplikat Kamar {room.number}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Buat beberapa kamar sekaligus dengan tipe harga yang sama. Pilih gedung tujuan (boleh gedung/proyek lain), lalu ketik nomor kamar baru, pisahkan dengan koma atau baris baru.</p>
          <div>
            <Label>Gedung Tujuan</Label>
            <Select value={targetBuilding} onValueChange={setTargetBuilding}>
              <SelectTrigger data-testid="gh-duplicate-target-building"><SelectValue placeholder="Pilih gedung" /></SelectTrigger>
              <SelectContent>{buildings.map((b) => <SelectItem key={b.id} value={b.id}>{b.project_name} · {b.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Nomor Kamar Baru</Label>
            <Textarea value={numbersRaw} onChange={(e) => setNumbersRaw(e.target.value)} placeholder="mis. G4, G5, G6" data-testid="gh-duplicate-numbers" />
            {parsed.length > 0 && <p className="mt-1 text-xs text-muted-foreground">{parsed.length} kamar akan dibuat: {parsed.join(", ")}</p>}
          </div>
          <div><Label>Harga per Malam</Label><Input type="number" value={daily} onChange={(e) => setDaily(e.target.value)} data-testid="gh-duplicate-daily" /></div>
          <div className="rounded-lg border border-dashed border-border p-3">
            <div className="mb-2 text-xs font-medium text-muted-foreground">Paket bulanan + IPL (disalin dari Kamar {room.number}, opsional)</div>
            <div className="grid grid-cols-2 gap-2">
              {["1", "3", "6", "12"].map((m) => <div key={m}><Label className="text-xs">{m} bln</Label><Input type="number" value={p[m]} onChange={(e) => setP((s) => ({ ...s, [m]: e.target.value }))} data-testid={`gh-duplicate-price-${m}`} /></div>)}
            </div>
            <div className="mt-2"><Label className="text-xs">IPL / bulan</Label><Input type="number" value={p.ipl} onChange={(e) => setP((s) => ({ ...s, ipl: e.target.value }))} data-testid="gh-duplicate-ipl" /></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={loading} data-testid="gh-duplicate-save">Buat Kamar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateRoom({ buildingId, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ number: "", daily_price: "", 1: "", 3: "", 6: "", 12: "", ipl: "" });
  const save = async () => {
    try {
      const pricing = (f[1] && f[3] && f[6] && f[12]) ? { 1: Number(f[1]), 3: Number(f[3]), 6: Number(f[6]), 12: Number(f[12]) } : null;
      await api.post("/guesthouse/rooms", { building_id: buildingId, number: f.number, daily_price: Number(f.daily_price), pricing, ipl_monthly: Number(f.ipl || 0) });
      toast.success("Kamar ditambahkan."); setOpen(false); setF({ number: "", daily_price: "", 1: "", 3: "", 6: "", 12: "", ipl: "" }); onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" size="sm" data-testid="gh-add-room-button"><Plus className="mr-1.5 h-4 w-4" /> Tambah Kamar</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Tambah Kamar Guest House</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nomor Kamar</Label><Input value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} data-testid="gh-room-number" /></div>
          <div><Label>Harga per Malam</Label><Input type="number" value={f.daily_price} onChange={(e) => setF({ ...f, daily_price: e.target.value })} data-testid="gh-room-daily" /></div>
          <div className="rounded-lg border border-dashed border-border p-3">
            <div className="mb-2 text-xs font-medium text-muted-foreground">Paket bulanan (opsional — untuk sewa 1/3/6/12 bln, kena IPL)</div>
            <div className="grid grid-cols-2 gap-2">
              {["1", "3", "6", "12"].map((m) => <div key={m}><Label className="text-xs">{m} bln</Label><Input type="number" value={f[m]} onChange={(e) => setF({ ...f, [m]: e.target.value })} data-testid={`gh-room-price-${m}`} /></div>)}
            </div>
            <div className="mt-2"><Label className="text-xs">IPL / bulan</Label><Input type="number" value={f.ipl} onChange={(e) => setF({ ...f, ipl: e.target.value })} data-testid="gh-room-ipl" /></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={save} data-testid="gh-room-save">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditRoomDialog({ room, isSuperadmin, onClose, onDone }) {
  const [number, setNumber] = useState(room.number);
  const [daily, setDaily] = useState(room.daily_price);
  const [p, setP] = useState({ 1: room.pricing?.["1"] || "", 3: room.pricing?.["3"] || "", 6: room.pricing?.["6"] || "", 12: room.pricing?.["12"] || "", ipl: room.ipl_monthly || "" });
  const [cond, setCond] = useState(room.physical_condition || "siap");
  const [note, setNote] = useState(room.prep_note || "");
  const [loading, setLoading] = useState(false);
  const save = async () => {
    if (!String(number).trim()) { toast.error("Nomor kamar wajib diisi."); return; }
    if (isSuperadmin && cond === "disiapkan" && !note.trim()) { toast.error("Keterangan wajib diisi saat kamar sedang disiapkan."); return; }
    const filled = ["1", "3", "6", "12"].filter((m) => p[m]).length;
    if (filled > 0 && filled < 4) { toast.error("Semua tarif paket (1, 3, 6, 12 bln) wajib diisi."); return; }
    if (Number(p.ipl || 0) < 0 || Number(daily) <= 0) { toast.error("Harga per malam dan IPL tidak valid."); return; }
    setLoading(true);
    try {
      const body = { daily_price: Number(daily), ipl_monthly: Number(p.ipl || 0) };
      if (isSuperadmin) Object.assign(body, { number: String(number).trim(), physical_condition: cond, prep_note: note });
      if (p[1] && p[3] && p[6] && p[12]) body.pricing = { 1: Number(p[1]), 3: Number(p[3]), 6: Number(p[6]), 12: Number(p[12]) };
      await api.patch(`/guesthouse/rooms/${room.id}`, body);
      toast.success(isSuperadmin ? "Kamar diperbarui." : "Harga kamar diperbarui."); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isSuperadmin ? "Ubah Kamar" : "Ubah Harga Kamar"} {room.number}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {isSuperadmin && <div><Label>Nomor Kamar</Label><Input value={number} onChange={(e) => setNumber(e.target.value)} data-testid="gh-edit-number" /></div>}
          <div><Label>Harga per Malam</Label><Input type="number" value={daily} onChange={(e) => setDaily(e.target.value)} data-testid="gh-edit-daily" /></div>
          <div className="rounded-lg border border-dashed border-border p-3">
            <div className="mb-2 text-xs font-medium text-muted-foreground">Paket bulanan + IPL</div>
            <div className="grid grid-cols-2 gap-2">
              {["1", "3", "6", "12"].map((m) => <div key={m}><Label className="text-xs">{m} bln</Label><Input type="number" value={p[m]} onChange={(e) => setP((s) => ({ ...s, [m]: e.target.value }))} data-testid={`gh-edit-price-${m}`} /></div>)}
            </div>
            <div className="mt-2"><Label className="text-xs">IPL / bulan</Label><Input type="number" value={p.ipl} onChange={(e) => setP((s) => ({ ...s, ipl: e.target.value }))} data-testid="gh-edit-ipl" /></div>
          </div>
          {isSuperadmin && <div><Label>Kondisi Fisik</Label>
            <Select value={cond} onValueChange={setCond}>
              <SelectTrigger data-testid="gh-edit-cond"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="siap">Siap Digunakan</SelectItem><SelectItem value="disiapkan">Sedang Disiapkan</SelectItem></SelectContent>
            </Select>
          </div>}
          {isSuperadmin && cond === "disiapkan" && <div><Label>Keterangan</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="gh-edit-note" /></div>}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={loading} data-testid="gh-edit-save">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecordStayDialog({ room, onClose, onDone }) {
  const [idem] = useState(uuid);
  const [f, setF] = useState({ guest_name: "", whatsapp: "", check_in: today(), check_out: "", price_per_night: room.daily_price, method: "transfer", received_at: nowLocalInput(), reference: "", note: "" });
  const [loading, setLoading] = useState(false);
  const nights = useMemo(() => {
    if (!f.check_in || !f.check_out) return 0;
    const n = Math.round((new Date(f.check_out + "T00:00:00") - new Date(f.check_in + "T00:00:00")) / 86400000);
    return n > 0 ? n : 0;
  }, [f.check_in, f.check_out]);
  const total = nights * (Number(f.price_per_night) || 0);
  const save = async () => {
    if (!f.guest_name.trim()) { toast.error("Nama tamu wajib diisi."); return; }
    if (nights < 1) { toast.error("Tanggal check-out harus setelah check-in (minimal 1 malam)."); return; }
    if (!Number(f.price_per_night)) { toast.error("Harga per malam wajib diisi."); return; }
    setLoading(true);
    try {
      const res = await api.post("/guesthouse/stays", {
        room_id: room.id, guest_name: f.guest_name, whatsapp: f.whatsapp, check_in: f.check_in, check_out: f.check_out,
        price_per_night: Number(f.price_per_night), method: f.method, received_at: new Date(f.received_at).toISOString(),
        reference: f.reference, note: f.note, idempotency_key: idem,
      });
      toast.success("Tamu dicatat.", { description: `Struk ${res.data.receipt_number}` }); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="gh-record-dialog">
        <DialogHeader><DialogTitle>Catat Tamu Harian — Kamar {room.number}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground">{room.building_name}</div>
          <div><Label>Nama Tamu</Label><Input value={f.guest_name} onChange={(e) => setF({ ...f, guest_name: e.target.value })} data-testid="gh-stay-guest" /></div>
          <div><Label>WhatsApp (opsional)</Label><Input value={f.whatsapp} onChange={(e) => setF({ ...f, whatsapp: e.target.value })} data-testid="gh-stay-wa" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Check-in</Label><Input type="date" value={f.check_in} onChange={(e) => setF({ ...f, check_in: e.target.value })} data-testid="gh-stay-checkin" /></div>
            <div><Label>Check-out</Label><Input type="date" value={f.check_out} min={f.check_in} onChange={(e) => setF({ ...f, check_out: e.target.value })} data-testid="gh-stay-checkout" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Harga per Malam</Label><Input type="number" value={f.price_per_night} onChange={(e) => setF({ ...f, price_per_night: e.target.value })} data-testid="gh-stay-price" /></div>
            <div><Label>Metode</Label>
              <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}>
                <SelectTrigger data-testid="gh-stay-method"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="transfer">Transfer Bank</SelectItem><SelectItem value="cash">Tunai</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Waktu Pembayaran Diterima</Label><Input type="datetime-local" value={f.received_at} onChange={(e) => setF({ ...f, received_at: e.target.value })} data-testid="gh-stay-received" /></div>
          <div><Label>Catatan (opsional)</Label><Textarea value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="gh-stay-note" /></div>
          <div className="rounded-lg bg-accent/40 p-3 text-sm" data-testid="gh-stay-summary">
            <div className="flex justify-between"><span className="text-muted-foreground">Jumlah malam</span><span className="font-semibold">{nights} malam</span></div>
            <div className="mt-1 flex justify-between"><span className="text-muted-foreground">Total</span><span className="font-extrabold text-primary"><Money value={total} /></span></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={loading} data-testid="gh-stay-save">Simpan & Buat Struk</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function addDaysIso(iso, days) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function WeeklyStayDialog({ room, onClose, onDone }) {
  const [idem] = useState(uuid);
  const [f, setF] = useState({ guest_name: "", whatsapp: "", check_in: today(), weeks: "1", price_per_week: "", method: "transfer", received_at: nowLocalInput(), note: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const weeks = Math.max(0, parseInt(f.weeks, 10) || 0);
  const price = Number(f.price_per_week) || 0;
  const checkOut = weeks > 0 ? addDaysIso(f.check_in, weeks * 7) : "";
  const save = async () => {
    if (!f.guest_name.trim()) { toast.error("Nama tamu wajib diisi."); return; }
    if (weeks < 1 || weeks > 52) { toast.error("Jumlah minggu 1 sampai 52."); return; }
    if (price <= 0) { toast.error("Harga per minggu wajib diisi."); return; }
    setLoading(true);
    try {
      const res = await api.post("/guesthouse/stays", {
        room_id: room.id, kind: "weekly", guest_name: f.guest_name, whatsapp: f.whatsapp, check_in: f.check_in,
        weeks, price_per_week: price, method: f.method, received_at: new Date(f.received_at).toISOString(), note: f.note, idempotency_key: idem,
      });
      toast.success("Tamu mingguan dicatat.", { description: `Struk ${res.data.receipt_number}` }); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="gh-weekly-dialog">
        <DialogHeader><DialogTitle>Catat Tamu Mingguan — Kamar {room.number}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground">{room.building_name} · Dibayar lunas di depan.</div>
          <div><Label>Nama Tamu</Label><Input value={f.guest_name} onChange={set("guest_name")} data-testid="gh-weekly-guest" /></div>
          <div><Label>WhatsApp (opsional)</Label><Input value={f.whatsapp} onChange={set("whatsapp")} data-testid="gh-weekly-wa" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Check-in</Label><Input type="date" value={f.check_in} onChange={set("check_in")} data-testid="gh-weekly-checkin" /></div>
            <div><Label>Jumlah Minggu</Label><Input type="number" min="1" max="52" value={f.weeks} onChange={set("weeks")} data-testid="gh-weekly-weeks" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Harga per Minggu (Rp)</Label><Input type="number" min="1" value={f.price_per_week} onChange={set("price_per_week")} placeholder="Isi harga" data-testid="gh-weekly-price" /></div>
            <div><Label>Metode</Label>
              <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}>
                <SelectTrigger data-testid="gh-weekly-method"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="transfer">Transfer Bank</SelectItem><SelectItem value="cash">Tunai</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Waktu Pembayaran Diterima</Label><Input type="datetime-local" value={f.received_at} onChange={set("received_at")} data-testid="gh-weekly-received" /></div>
          <div><Label>Catatan (opsional)</Label><Textarea value={f.note} onChange={set("note")} data-testid="gh-weekly-note" /></div>
          <div className="rounded-lg bg-accent/40 p-3 text-sm space-y-1" data-testid="gh-weekly-summary">
            <div className="flex justify-between"><span className="text-muted-foreground">Check-out</span><span className="font-semibold" data-testid="gh-weekly-checkout">{checkOut ? formatTanggal(checkOut) : "-"}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Lama sewa</span><span className="font-semibold">{weeks} minggu ({weeks * 7} malam)</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span className="font-extrabold text-primary" data-testid="gh-weekly-total"><Money value={weeks * price} /></span></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={loading} data-testid="gh-weekly-save">Simpan & Buat Struk</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExtendWeeklyDialog({ stay, onClose, onDone }) {
  const [idem] = useState(uuid);
  const [f, setF] = useState({ weeks: "1", price_per_week: stay.price_per_week ? String(stay.price_per_week) : "", method: "transfer", received_at: nowLocalInput(), note: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const weeks = Math.max(0, parseInt(f.weeks, 10) || 0);
  const price = Number(f.price_per_week) || 0;
  const start = String(stay.check_out || "").slice(0, 10);
  const newCheckOut = weeks > 0 ? addDaysIso(start, weeks * 7) : "";
  const save = async () => {
    if (weeks < 1 || weeks > 52) { toast.error("Jumlah minggu 1 sampai 52."); return; }
    if (price <= 0) { toast.error("Harga per minggu wajib diisi."); return; }
    setLoading(true);
    try {
      const res = await api.post(`/guesthouse/stays/${stay.id}/extend`, {
        weeks, price_per_week: price, method: f.method, received_at: new Date(f.received_at).toISOString(), note: f.note, idempotency_key: idem,
      });
      toast.success("Sewa mingguan diperpanjang.", { description: `Struk ${res.data.receipt_number}` }); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="gh-extend-dialog">
        <DialogHeader><DialogTitle>Perpanjang Sewa Mingguan — Kamar {stay.room_number}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg border border-border p-3 text-sm" data-testid="gh-extend-guest">
            <div className="font-semibold">{stay.guest_name}</div>
            <div className="text-xs text-muted-foreground">{stay.building_name} · Check-out saat ini {formatTanggal(start)}</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Tambah Minggu</Label><Input type="number" min="1" max="52" value={f.weeks} onChange={set("weeks")} data-testid="gh-extend-weeks" /></div>
            <div><Label>Harga per Minggu (Rp)</Label><Input type="number" min="1" value={f.price_per_week} onChange={set("price_per_week")} placeholder="Isi harga" data-testid="gh-extend-price" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Metode</Label>
              <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}>
                <SelectTrigger data-testid="gh-extend-method"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="transfer">Transfer Bank</SelectItem><SelectItem value="cash">Tunai</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Waktu Pembayaran</Label><Input type="datetime-local" value={f.received_at} onChange={set("received_at")} data-testid="gh-extend-received" /></div>
          </div>
          <div><Label>Catatan (opsional)</Label><Textarea value={f.note} onChange={set("note")} data-testid="gh-extend-note" /></div>
          <div className="rounded-lg bg-accent/40 p-3 text-sm space-y-1" data-testid="gh-extend-summary">
            <div className="flex justify-between"><span className="text-muted-foreground">Check-out baru</span><span className="font-semibold" data-testid="gh-extend-new-checkout">{newCheckOut ? formatTanggal(newCheckOut) : "-"}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Tambahan</span><span className="font-semibold">{weeks} minggu ({weeks * 7} malam)</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Total Dibayar</span><span className="font-extrabold text-primary" data-testid="gh-extend-total"><Money value={weeks * price} /></span></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={loading} data-testid="gh-extend-save">Simpan & Buat Struk</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MonthlyBookingDialog({ room, onClose, onDone }) {
  const idem = useRef(uuid());
  const [pkg, setPkg] = useState("3");
  const [f, setF] = useState({ guest_name: "", whatsapp: "", rent_price: room.pricing?.["3"] || "", method: "transfer", received: nowLocalInput(), reference: "" });
  const [loading, setLoading] = useState(false);
  const onPkg = (m) => { setPkg(m); setF((s) => ({ ...s, rent_price: room.pricing?.[m] || "" })); };
  const rent = Number(f.rent_price) || 0;
  const ipl = Number(room.ipl_monthly || 0) * Number(pkg);
  const total = rent + ipl;
  const dp = 500000;
  const submit = async () => {
    if (!f.guest_name.trim() || !f.whatsapp.trim()) { toast.error("Lengkapi nama & WhatsApp."); return; }
    if (!rent) { toast.error("Harga sewa wajib diisi."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/guesthouse/bookings", {
        room_id: room.id, guest_name: f.guest_name, whatsapp: f.whatsapp, package_months: Number(pkg),
        rent_price: rent, dp_method: f.method, dp_received_at: toIsoWib(f.received), reference: f.reference, idempotency_key: idem.current,
      });
      toast.success("Booking bulanan dibuat & struk DP diterbitkan.", { description: `Struk ${data.receipt_number}` }); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="gh-monthly-dialog">
        <DialogHeader><DialogTitle>Booking Bulanan — Kamar {room.number}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground">{room.building_name}</div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Nama Calon Penghuni</Label><Input value={f.guest_name} onChange={(e) => setF({ ...f, guest_name: e.target.value })} data-testid="gh-monthly-name" /></div>
            <div><Label>WhatsApp</Label><Input value={f.whatsapp} onChange={(e) => setF({ ...f, whatsapp: e.target.value })} data-testid="gh-monthly-wa" /></div>
          </div>
          <div><Label>Paket Sewa</Label>
            <div className="mt-1 flex gap-2">
              {["1", "3", "6", "12"].map((m) => <button key={m} onClick={() => onPkg(m)} data-testid={`gh-monthly-pkg-${m}`} className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium ${pkg === m ? "border-primary bg-primary text-white" : "border-border bg-card"}`}>{m} bulan</button>)}
            </div>
          </div>
          <div><Label>Harga Sewa ({pkg} bln) — bisa diubah</Label><Input type="number" value={f.rent_price} onChange={(e) => setF({ ...f, rent_price: e.target.value })} data-testid="gh-monthly-rent" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Metode DP</Label>
              <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}>
                <SelectTrigger data-testid="gh-monthly-method"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="transfer">Transfer Bank</SelectItem><SelectItem value="cash">Tunai</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Waktu DP Diterima (WIB)</Label><Input type="datetime-local" value={f.received} onChange={(e) => setF({ ...f, received: e.target.value })} data-testid="gh-monthly-received" /></div>
          </div>
          <div className="rounded-xl border border-border bg-muted/30 p-4 text-sm" data-testid="gh-monthly-summary">
            <div className="space-y-1">
              <SummaryRow label="Harga Sewa" value={rent} />
              <SummaryRow label={`IPL (${pkg} bln)`} value={ipl} />
              <SummaryRow label="Total Tagihan" value={total} bold />
              <SummaryRow label="DP" value={dp} />
              <SummaryRow label="Sisa Pelunasan" value={total - dp} bold />
            </div>
            <div className="mt-2 text-xs text-muted-foreground">Kamar ditahan 96 jam sejak DP diterima. DP menjadi bagian pembayaran sewa.</div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={submit} disabled={loading || !rent} data-testid="gh-monthly-submit">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Simpan Booking & Struk DP</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SettlementDialog({ booking, onClose, onDone }) {
  const idem = useRef(uuid());
  const [f, setF] = useState({ full_name_ktp: booking.guest_name, nik: "", address_ktp: "", guardian_whatsapp: "", whatsapp: booking.whatsapp, method: "transfer", received: nowLocalInput(), plan: "" });
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!/^\d{16}$/.test(f.nik.trim())) { toast.error("NIK harus 16 digit angka."); return; }
    if (!f.full_name_ktp.trim() || !f.address_ktp.trim() || !f.guardian_whatsapp.trim()) { toast.error("Lengkapi data identitas wajib."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/guesthouse/settlements", {
        booking_id: booking.id, amount: booking.remaining, method: f.method, received_at: toIsoWib(f.received),
        plan_movein_date: f.plan || null, full_name_ktp: f.full_name_ktp, nik: f.nik, address_ktp: f.address_ktp,
        guardian_whatsapp: f.guardian_whatsapp, whatsapp: f.whatsapp, idempotency_key: idem.current,
      });
      toast.success("Pelunasan tercatat — status menjadi Menunggu Masuk.", { description: `Struk ${data.receipt_number}` }); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="gh-settlement-dialog">
        <DialogHeader><DialogTitle>Catat Pelunasan — {booking.guest_name}</DialogTitle></DialogHeader>
        <div className="rounded-lg bg-muted/40 p-3 text-sm">Kamar {booking.room_number} · {booking.package_months} bln · Sisa: <b><Money value={booking.remaining} /></b></div>
        <div className="mt-3 space-y-3">
          <div className="text-xs font-semibold uppercase text-muted-foreground">Data Identitas (wajib)</div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><Label>Nama sesuai KTP</Label><Input value={f.full_name_ktp} onChange={(e) => setF({ ...f, full_name_ktp: e.target.value })} data-testid="gh-settle-fullname" /></div>
            <div><Label>NIK (16 digit)</Label><Input value={f.nik} onChange={(e) => setF({ ...f, nik: e.target.value })} data-testid="gh-settle-nik" /></div>
          </div>
          <div><Label>Alamat sesuai KTP</Label><Textarea value={f.address_ktp} onChange={(e) => setF({ ...f, address_ktp: e.target.value })} data-testid="gh-settle-address" /></div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><Label>WhatsApp Penghuni</Label><Input value={f.whatsapp} onChange={(e) => setF({ ...f, whatsapp: e.target.value })} data-testid="gh-settle-whatsapp" /></div>
            <div><Label>WhatsApp Wali</Label><Input value={f.guardian_whatsapp} onChange={(e) => setF({ ...f, guardian_whatsapp: e.target.value })} data-testid="gh-settle-guardian" /></div>
          </div>
          <div className="text-xs font-semibold uppercase text-muted-foreground">Pembayaran</div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><Label>Metode</Label>
              <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}>
                <SelectTrigger data-testid="gh-settle-method"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="transfer">Transfer Bank</SelectItem><SelectItem value="cash">Tunai</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Waktu Diterima (WIB)</Label><Input type="datetime-local" value={f.received} onChange={(e) => setF({ ...f, received: e.target.value })} data-testid="gh-settle-received" /></div>
          </div>
          <div><Label>Rencana Masuk (opsional)</Label><Input type="date" value={f.plan} onChange={(e) => setF({ ...f, plan: e.target.value })} data-testid="gh-settle-plan" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={submit} disabled={loading} data-testid="gh-settle-submit">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Catat Pelunasan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GhDirectRentDialog({ room, onClose, onDone }) {
  const idem = useRef(uuid());
  const [pkg, setPkg] = useState("3");
  const [f, setF] = useState({
    guest_name: "", whatsapp: "", rent_price: room.pricing?.["3"] || "", discount: "", extra_fees: "", deposit: "1000000",
    full_name_ktp: "", nik: "", address_ktp: "", guardian_whatsapp: "", method: "transfer", received: nowLocalInput(), plan: today(), note: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const onPkg = (m) => { setPkg(m); setF((s) => ({ ...s, rent_price: room.pricing?.[m] || "" })); };
  const rent = Number(f.rent_price) || 0;
  const discount = Number(f.discount) || 0;
  const extra = Number(f.extra_fees) || 0;
  const deposit = Number(f.deposit) || 0;
  const ipl = Number(room.ipl_monthly || 0) * Number(pkg);
  const total = rent - discount + ipl + extra + deposit;
  const submit = async () => {
    if (!f.guest_name.trim() || !f.whatsapp.trim()) { toast.error("Lengkapi nama & WhatsApp tamu."); return; }
    if (!rent) { toast.error("Harga sewa wajib diisi."); return; }
    if (discount < 0 || extra < 0 || deposit < 0) { toast.error("Diskon, biaya tambahan, dan deposit tidak boleh negatif."); return; }
    if (discount > rent) { toast.error("Diskon tidak boleh melebihi harga sewa."); return; }
    if (!f.full_name_ktp.trim() || !f.address_ktp.trim() || !f.guardian_whatsapp.trim()) { toast.error("Lengkapi data identitas wajib."); return; }
    if (!/^\d{16}$/.test(f.nik.trim())) { toast.error("NIK harus 16 digit angka."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/guesthouse/direct-rent", {
        room_id: room.id, guest_name: f.guest_name, whatsapp: f.whatsapp, package_months: Number(pkg), rent_price: rent,
        discount, extra_fees: extra, deposit, amount: total, method: f.method, received_at: toIsoWib(f.received),
        plan_movein_date: f.plan || null, full_name_ktp: f.full_name_ktp, nik: f.nik.trim(), address_ktp: f.address_ktp,
        guardian_whatsapp: f.guardian_whatsapp, note: f.note, idempotency_key: idem.current,
      });
      toast.success("Langsung sewa tercatat — status Menunggu Masuk.", { description: `Struk ${data.receipt_number}` }); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="gh-direct-dialog">
        <DialogHeader><DialogTitle>Langsung Sewa (Tanpa DP) — Kamar {room.number}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground">{room.building_name} · Dibayar penuh sekaligus. Kamar berstatus Menunggu Masuk sampai Anda mencatat tanggal masuk.</div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><Label>Nama Tamu</Label><Input value={f.guest_name} onChange={set("guest_name")} data-testid="gh-direct-name" /></div>
            <div><Label>WhatsApp</Label><Input value={f.whatsapp} onChange={set("whatsapp")} data-testid="gh-direct-wa" /></div>
          </div>
          <div><Label>Paket Sewa</Label>
            <div className="mt-1 flex gap-2">
              {["1", "3", "6", "12"].map((m) => <button key={m} onClick={() => onPkg(m)} data-testid={`gh-direct-pkg-${m}`} className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium ${pkg === m ? "border-primary bg-primary text-white" : "border-border bg-card"}`}>{m} bln</button>)}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><Label>Harga Sewa ({pkg} bln) — bisa diubah</Label><Input type="number" value={f.rent_price} onChange={set("rent_price")} data-testid="gh-direct-rent" /></div>
            <div><Label>Diskon (Rp)</Label><Input type="number" min="0" value={f.discount} onChange={set("discount")} placeholder="0" data-testid="gh-direct-discount" /></div>
            <div><Label>Biaya Tambahan (Rp)</Label><Input type="number" min="0" value={f.extra_fees} onChange={set("extra_fees")} placeholder="0" data-testid="gh-direct-extra" /></div>
            <div><Label>Deposit (Rp, refundable)</Label><Input type="number" min="0" value={f.deposit} onChange={set("deposit")} data-testid="gh-direct-deposit" /></div>
          </div>
          <div className="text-xs font-semibold uppercase text-muted-foreground">Data Identitas (wajib)</div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><Label>Nama sesuai KTP</Label><Input value={f.full_name_ktp} onChange={set("full_name_ktp")} data-testid="gh-direct-fullname" /></div>
            <div><Label>NIK (16 digit)</Label><Input value={f.nik} onChange={set("nik")} data-testid="gh-direct-nik" /></div>
          </div>
          <div><Label>Alamat sesuai KTP</Label><Textarea value={f.address_ktp} onChange={set("address_ktp")} data-testid="gh-direct-address" /></div>
          <div><Label>WhatsApp Wali</Label><Input value={f.guardian_whatsapp} onChange={set("guardian_whatsapp")} data-testid="gh-direct-guardian" /></div>
          <div className="text-xs font-semibold uppercase text-muted-foreground">Pembayaran</div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div><Label>Metode</Label>
              <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}>
                <SelectTrigger data-testid="gh-direct-method"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="transfer">Transfer Bank</SelectItem><SelectItem value="cash">Tunai</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Waktu Diterima (WIB)</Label><Input type="datetime-local" value={f.received} onChange={set("received")} data-testid="gh-direct-received" /></div>
            <div><Label>Rencana Masuk</Label><Input type="date" value={f.plan} onChange={set("plan")} data-testid="gh-direct-plan" /></div>
          </div>
          <div><Label>Catatan (opsional)</Label><Textarea value={f.note} onChange={set("note")} data-testid="gh-direct-note" /></div>
          <div className="rounded-xl border border-border bg-muted/30 p-4 text-sm space-y-1" data-testid="gh-direct-summary">
            <SummaryRow label="Harga Sewa" value={rent} />
            {discount > 0 && <SummaryRow label="Diskon" value={-discount} />}
            <SummaryRow label={`IPL (${pkg} bln)`} value={ipl} />
            {extra > 0 && <SummaryRow label="Biaya Tambahan" value={extra} />}
            {deposit > 0 && <SummaryRow label="Deposit (refundable)" value={deposit} />}
            <div data-testid="gh-direct-total"><SummaryRow label="Total Dibayar Penuh" value={total} bold /></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={submit} disabled={loading || !rent} data-testid="gh-direct-submit">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Simpan & Buat Struk</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GhDepositRefundDialog({ lease, onClose, onDone }) {
  const [f, setF] = useState({ amount: String(lease.deposit || 0), date: today(), method: "transfer", note: "" });
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    const amt = Number(f.amount);
    if (!amt || amt <= 0 || amt > Number(lease.deposit)) { toast.error("Nominal harus antara Rp1 dan nilai deposit."); return; }
    setLoading(true);
    try {
      await api.post(`/guesthouse/leases/${lease.id}/deposit-refund`, { amount: amt, date: f.date, method: f.method, note: f.note });
      toast.success("Pengembalian deposit tercatat & masuk Keuangan Operasional (kas keluar)."); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="gh-deposit-refund-dialog">
        <DialogHeader><DialogTitle>Kembalikan Deposit — {lease.guest_name}</DialogTitle></DialogHeader>
        <div className="rounded-lg bg-muted/40 p-3 text-sm">Kamar {lease.room_number} · Deposit ditahan: <b><Money value={lease.deposit} /></b>
          <div className="mt-0.5 text-xs text-muted-foreground">Nominal dapat dikurangi (potongan kerusakan dll).</div>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div><Label>Nominal Dikembalikan (Rp)</Label><Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="gh-deposit-refund-amount" /></div>
          <div><Label>Tanggal</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="gh-deposit-refund-date" /></div>
        </div>
        <div><Label>Metode</Label>
          <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}>
            <SelectTrigger data-testid="gh-deposit-refund-method"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="transfer">Transfer</SelectItem><SelectItem value="cash">Tunai</SelectItem></SelectContent>
          </Select>
        </div>
        <div><Label>Catatan (opsional)</Label><Textarea value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="gh-deposit-refund-note" /></div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={submit} disabled={loading} data-testid="gh-deposit-refund-confirm">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Catat Pengembalian</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MoveinDialog({ lease, onClose, onDone }) {
  const [date, setDate] = useState(lease.plan_movein_date || today());
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try { const { data } = await api.post(`/guesthouse/leases/${lease.id}/movein`, { actual_movein_date: date }); toast.success("Penghuni tercatat masuk.", { description: `Periode: ${data.period_label}` }); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="gh-movein-dialog">
        <DialogHeader><DialogTitle>Catat Pindah Masuk</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Masa sewa dimulai pada tanggal masuk aktual yang Anda catat.</p>
        <div><Label>Tanggal Masuk Aktual</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="gh-movein-date" /></div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={submit} disabled={loading} data-testid="gh-movein-submit">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExtensionDialog({ lease, onClose, onDone }) {
  const idem = useRef(uuid());
  const [pkg, setPkg] = useState("3");
  const [rent, setRent] = useState("");
  const [method, setMethod] = useState("transfer");
  const [received, setReceived] = useState(nowLocalInput());
  const [loading, setLoading] = useState(false);
  const { data: roomData } = useQuery({ queryKey: ["gh-room-detail", lease.room_id], queryFn: async () => (await api.get("/guesthouse/rooms", { params: { building_id: lease.building_id } })).data });
  const room = (roomData || []).find((r) => r.id === lease.room_id);
  const defaultRent = room?.pricing?.[pkg] ?? lease.rent_price;
  const rentVal = rent === "" ? Number(defaultRent) : Number(rent);
  const iplTotal = Number(lease.ipl_monthly || 0) * Number(pkg);
  const amount = rentVal + iplTotal;
  const submit = async () => {
    setLoading(true);
    try {
      await api.post(`/guesthouse/leases/${lease.id}/extensions`, { package_months: Number(pkg), amount, rent_price: rentVal, method, received_at: toIsoWib(received), idempotency_key: idem.current });
      toast.success("Perpanjangan tercatat & lunas."); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="gh-extension-dialog">
        <DialogHeader><DialogTitle>Perpanjangan Sewa — {lease.guest_name}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Periode baru menyambung periode berjalan. Dibayar penuh (tanpa DP).</p>
        <div><Label>Durasi</Label>
          <div className="mt-1 flex gap-2">
            {["1", "3", "6", "12"].map((m) => <button key={m} onClick={() => { setPkg(m); setRent(""); }} data-testid={`gh-ext-pkg-${m}`} className={`flex-1 rounded-lg border px-3 py-2 text-sm ${pkg === m ? "border-primary bg-primary text-white" : "border-border"}`}>{m} bln</button>)}
          </div>
        </div>
        <div><Label>Harga Sewa ({pkg} bln) — bisa diubah</Label><Input type="number" value={rent === "" ? defaultRent : rent} onChange={(e) => setRent(e.target.value)} data-testid="gh-ext-rent" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Metode</Label>
            <Select value={method} onValueChange={setMethod}><SelectTrigger data-testid="gh-ext-method"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="transfer">Transfer Bank</SelectItem><SelectItem value="cash">Tunai</SelectItem></SelectContent></Select>
          </div>
          <div><Label>Waktu Diterima (WIB)</Label><Input type="datetime-local" value={received} onChange={(e) => setReceived(e.target.value)} data-testid="gh-ext-received" /></div>
        </div>
        <div className="rounded-lg bg-muted/40 p-3 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Harga sewa {pkg} bln</span><Money value={rentVal} /></div>
          <div className="flex justify-between"><span className="text-muted-foreground">IPL ({pkg} bln)</span><Money value={iplTotal} /></div>
          <div className="mt-1 flex justify-between font-semibold"><span>Total pelunasan</span><Money value={amount} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={submit} disabled={loading || !rentVal} data-testid="gh-ext-submit">Catat Perpanjangan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExitDialog({ lease, onClose, onDone }) {
  const [date, setDate] = useState(today());
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!reason.trim()) { toast.error("Alasan wajib diisi."); return; }
    setLoading(true);
    try { await api.post(`/guesthouse/leases/${lease.id}/checkout-early`, { actual_checkout_date: date, reason }); toast.success("Penghuni keluar dicatat. Kamar kembali tersedia."); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="gh-exit-dialog">
        <DialogHeader><DialogTitle>Keluar Lebih Awal — {lease.guest_name}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Tidak ada refund sewa maupun IPL. Riwayat tetap tersimpan.{lease.deposit > 0 && " Deposit dapat dikembalikan setelah proses keluar."}</p>
        <div><Label>Tanggal Keluar Aktual</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="gh-exit-date" /></div>
        <div><Label>Alasan</Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="gh-exit-reason" /></div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={submit} disabled={loading} data-testid="gh-exit-submit">Proses Keluar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
